import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const startTimer = () => {
    setTimer(30);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) clearInterval(intervalRef.current);
        return t - 1;
      });
    }, 1000);
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.otp_required) {
        setStep("otp");
        startTimer();
        if (data.email_sent === false) {
          toast.warning("Email service is busy — tap Resend in a few seconds if the code doesn't arrive");
        } else {
          toast.success("OTP sent to your email");
        }
      } else {
        login(data.token, data.user);
        toast.success("Welcome back, Admin");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(apiErr(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      login(data.token, data.user);
      toast.success("Login successful");
      navigate(data.user.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(apiErr(err, "Invalid OTP"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-otp", { email, password });
      startTimer();
      if (data.email_sent === false) toast.warning("Email service busy, retrying — please wait a moment");
      else toast.success("OTP resent");
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950 text-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 bg-gradient-to-br from-blue-950 via-slate-950 to-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(50%_40%_at_30%_20%,rgba(37,99,235,0.3),transparent)]" />
        <div className="relative flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-blue-600 grid place-items-center font-heading font-extrabold text-lg">V</div>
          <span className="font-heading text-2xl font-extrabold tracking-tight">NaxoPay</span>
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">Collect & settle UPI payments with confidence.</h2>
          <p className="mt-4 text-slate-400 max-w-md">Static QR collections, triple wallets, realtime alerts and instant payouts — all from one secure dashboard.</p>
          <div className="mt-8 flex items-center gap-3 text-sm text-slate-300">
            <ShieldCheck className="h-5 w-5 text-emerald-400" /> OTP-protected, bank-grade security
          </div>
        </div>
        <div className="relative text-xs text-slate-600">© {new Date().getFullYear()} NaxoPay</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <button onClick={() => (step === "otp" ? setStep("credentials") : navigate("/"))} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-8" data-testid="login-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {step === "credentials" ? (
            <form onSubmit={submitCreds} className="space-y-5">
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight">Sign in</h1>
                <p className="text-sm text-slate-400 mt-1">Enter your credentials to continue</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" className="bg-slate-900 border-slate-800 h-11" data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-slate-900 border-slate-800 h-11" data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="login-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-6">
              <div className="h-12 w-12 rounded-2xl bg-blue-600/20 text-blue-400 grid place-items-center">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight">Verify your email</h1>
                <p className="text-sm text-slate-400 mt-1">We sent a 6-digit code to <span className="text-slate-200">{email}</span></p>
              </div>
              <div className="flex justify-center" data-testid="login-otp-input">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg bg-slate-900 border-slate-800" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button type="submit" disabled={loading || otp.length !== 6} className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="login-otp-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <div className="text-center text-sm text-slate-400">
                {timer > 0 ? (
                  <span>Resend code in {timer}s</span>
                ) : (
                  <button type="button" onClick={resend} className="text-blue-400 hover:underline" data-testid="login-resend-otp-btn">Resend code</button>
                )}
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
